/**
 * PassOpportunityModal Component
 * 
 * Web-compatible modal for passing on an opportunity with reason.
 * Replaces Alert.prompt which is iOS-only.
 */

import React, { useState } from 'react';
import { View, Text, Modal, TextInput, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import { color, space, radius, text } from '../../../shared/tokens/mobile';

interface PassOpportunityModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void>;
}

export function PassOpportunityModal({ visible, onCancel, onConfirm }: PassOpportunityModalProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(reason);
      setReason('');
    } catch {
      // Parent handles the error via Alert
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setReason('');
    onCancel();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={submitting ? undefined : handleCancel}>
      <View style={styles.overlay}>
        <View style={styles.container}>
          <Text style={styles.title}>Pass on Opportunity</Text>
          <Text style={styles.subtitle}>Why are you passing on this opportunity?</Text>
          <TextInput
            style={styles.input}
            value={reason}
            onChangeText={setReason}
            placeholder="Enter reason (optional)"
            placeholderTextColor={color.textMuted}
            multiline
            numberOfLines={3}
            editable={!submitting}
          />
          <View style={styles.buttons}>
            <Pressable 
              style={styles.cancelButton} 
              onPress={handleCancel}
              disabled={submitting}
            >
              <Text style={styles.cancelText}>Cancel</Text>
            </Pressable>
            <Pressable 
              style={[styles.confirmButton, submitting && styles.confirmButtonDisabled]} 
              onPress={handleConfirm}
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator size="small" color={color.surface} />
              ) : (
                <Text style={styles.confirmText}>Pass</Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: space.lg,
  },
  container: {
    backgroundColor: color.surface,
    borderRadius: radius.lg,
    padding: space.lg,
    width: '100%',
    maxWidth: 400,
  },
  title: {
    fontSize: text.cardTitle.fontSize,
    fontWeight: text.cardTitle.fontWeight,
    color: color.textPrimary,
    marginBottom: space.xs,
  },
  subtitle: {
    fontSize: text.body.fontSize,
    color: color.textMuted,
    marginBottom: space.md,
  },
  input: {
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.md,
    padding: space.md,
    fontSize: text.body.fontSize,
    color: color.textPrimary,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  buttons: {
    flexDirection: 'row',
    gap: space.sm,
    marginTop: space.lg,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: space.sm,
    backgroundColor: color.backgroundSecondary,
    borderRadius: radius.md,
    alignItems: 'center',
  },
  cancelText: {
    fontSize: text.body.fontSize,
    color: color.textPrimary,
  },
  confirmButton: {
    flex: 1,
    paddingVertical: space.sm,
    backgroundColor: color.error,
    borderRadius: radius.md,
    alignItems: 'center',
  },
  confirmButtonDisabled: {
    opacity: 0.6,
  },
  confirmText: {
    fontSize: text.body.fontSize,
    fontWeight: '600',
    color: color.surface,
  },
});

export default PassOpportunityModal;

